import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { StatusBadge } from "./StatusBadge";
import { FeaturedOrderManager } from "./FeaturedOrderManager";
import { mediaUrl } from "../../lib/mediaUrl";

const VERIFICATION_FILTERS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending Verification" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "suspended", label: "Suspended" },
];

export const PhotographersTab = () => {
  const navigate = useNavigate();
  const [photographers, setPhotographers] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("");
  const [location, setLocation] = useState("");

  const load = () => {
    api.get("/admin/photographers", { params: { verification_status: verificationStatus || undefined, location: location || undefined } }).then(({ data }) => setPhotographers(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStatus, location]);

  return (
    <div data-testid="photographers-tab">
      <FeaturedOrderManager />
      <div className="flex flex-wrap items-end gap-3 mb-6">
        {VERIFICATION_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`photographer-verification-filter-${f.value || "all"}`}
            onClick={() => setVerificationStatus(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${verificationStatus === f.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
        <Input data-testid="photographer-location-search" placeholder="Filter by location..." value={location} onChange={(e) => setLocation(e.target.value)} className="max-w-xs ml-auto" />
      </div>

      {photographers === null ? (
        <p className="font-medium text-midnight-black/60">Loading photographers...</p>
      ) : photographers.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-photographers">No photographers match.</p>
      ) : (
        <div className="space-y-3" data-testid="photographers-list">
          {photographers.map((p) => (
            <div
              key={p.id}
              onClick={() => navigate(`/admin/photographers/${p.id}`)}
              data-testid={`photographer-row-${p.id}`}
              className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap cursor-pointer hover:border-stranger-orange transition-colors"
            >
              <div className="flex items-center gap-3">
                {p.profile_photo_url ? (
                  <img src={mediaUrl(p.profile_photo_url)} alt={p.business_name} className="w-10 h-10 rounded-full object-cover shrink-0 bg-warm-white" data-testid={`photographer-thumbnail-${p.id}`} />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-soft-gray flex items-center justify-center font-bold text-xs shrink-0" data-testid={`photographer-thumbnail-placeholder-${p.id}`}>
                    {(p.first_name || "?")[0]}
                  </div>
                )}
                <div>
                <p className="font-extrabold text-sm uppercase">{p.first_name} {p.last_name} — {p.business_name}</p>
                <p className="text-xs font-medium text-midnight-black/60">{p.location || "No location set"} · {p.email}</p>
                <p className="text-xs font-medium text-midnight-black/50">{p.upcoming_sessions} upcoming · {p.completed_sessions} completed · {p.portfolio_count} portfolio photo{p.portfolio_count === 1 ? "" : "s"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {p.rating_avg && (
                  <span className="flex items-center gap-1 text-xs font-bold">
                    <Star className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" /> {p.rating_avg}
                  </span>
                )}
                <StatusBadge status={p.verification_status} testId={`photographer-verification-${p.id}`} />
                <StatusBadge status={p.account_status} testId={`photographer-account-status-${p.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
